import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import React, { useMemo, useState } from 'react';

import { BaseIcon } from 'src/components/BaseIcon';
import { TBaseDateRangePickerProps } from './BaseDateRangePicker.types';
import { UTCMoment } from 'src/utils/UTCMoment';
import classnames from 'classnames';
import s from './BaseDateRangePicker.module.scss';

export const BaseDateRangePicker: React.FC<TBaseDateRangePickerProps> = ({
  className,
  theme,
  startValue,
  endValue,
  valueFormat,
  onChange,
  disabled,
  minDate,
  maxDate,
  placeholderText,
  startValueName,
  endValueName,
  tags,
}) => {
  const [isCalendarOpened, setIsCalendarOpened] = useState<false | 'start' | 'end'>(false);

  const selectedRangeDaysCount = useMemo(() => {
    if (startValue && endValue) {
      return UTCMoment.utc(endValue).diff(UTCMoment.utc(startValue), 'days');
    }
    return 0;
  }, [endValue, startValue]);

  const handleChange = (date: Date, isStartVal: boolean) => {
    const value = UTCMoment.utc(
      // https://github.com/Hacker0x01/react-datepicker/issues/1787#issuecomment-770313939
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    ).format(valueFormat);

    onChange({
      startDate: isStartVal ? value : startValue,
      endDate: !isStartVal ? value : endValue,
    });
  };

  const handleBadgeClick = (value: number) => {
    const startDate = startValue || UTCMoment.utc().format(valueFormat);
    const endDate = UTCMoment.utc(startDate).add(value, 'days').format(valueFormat);
    onChange({
      startDate,
      endDate,
    });
  };

  const customHeader: ReactDatePickerProps['renderCustomHeader'] = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className={s.customHeaderContainer}>
      <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
        <BaseIcon icon="arrow-left" size={10} />
      </button>
      <span className="react-datepicker__current-month">
        {date.toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
        })}
      </span>
      <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
        <BaseIcon icon="arrow-left" size={10} style={{ transform: 'rotate(180deg)' }} />
      </button>
    </div>
  );

  return (
    <div
      className={classnames(className, {
        [s.container]: true,
        [s.themeDark]: theme === 'dark',
        [s.themeLight]: theme === 'light',
        [s.calendarOpened]: !!isCalendarOpened,
        [s.startCalendarActive]: isCalendarOpened === 'start',
        [s.endCalendarActive]: isCalendarOpened === 'end',
      })}>
      <div className={s.pickers}>
        <input type="hidden" name={startValueName} />
        <input type="hidden" name={endValueName} />
        <BaseIcon className={s.calendarIcon} icon="calendar" />
        <BaseIcon className={s.calendarIcon} icon="calendar" />
        <DatePicker
          className={s.datepickerInput}
          selected={
            typeof startValue === 'string'
              ? UTCMoment.utc(startValue, valueFormat).toDate()
              : startValue
          }
          onChange={(d) => handleChange(d as Date, true)}
          dateFormat="d MMMM"
          disabled={disabled}
          maxDate={maxDate}
          placeholderText={placeholderText}
          onCalendarOpen={() => {
            setIsCalendarOpened('start');
          }}
          onCalendarClose={() => {
            setIsCalendarOpened(false);
          }}
          renderCustomHeader={customHeader}
        />
        <DatePicker
          className={s.datepickerInput}
          selected={
            typeof endValue === 'string' ? UTCMoment.utc(endValue, valueFormat).toDate() : endValue
          }
          onChange={(d) => handleChange(d as Date, false)}
          dateFormat="d MMMM"
          disabled={disabled}
          minDate={minDate}
          placeholderText={placeholderText}
          onCalendarOpen={() => {
            setIsCalendarOpened('end');
          }}
          onCalendarClose={() => {
            setIsCalendarOpened(false);
          }}
          renderCustomHeader={customHeader}
        />
      </div>
      <div className={s.tags}>
        {tags?.map((item) => (
          <button
            key={item.value}
            disabled={disabled}
            type="button"
            onClick={() => handleBadgeClick(item.value)}
            className={classnames({
              [s.checked]: selectedRangeDaysCount === item.value,
            })}>
            {item.label}

            {item.badge && <div className={s.badge}>{item.badge}</div>}
          </button>
        ))}
      </div>
    </div>
  );
};
